// ═══════════════════════════════════════════════════════════════════════
// Temps › Revue des actes sensibles (RH, DRH) — conception § 15, § 10.1-5 : à revoir chaque
// semaine. Appels nominaux, saisies RH, corrections après clôture, actes « sans second regard »,
// scans refusés, consultations de certificats médicaux (qui, de qui, quand ; jamais le contenu).
// ═══════════════════════════════════════════════════════════════════════
import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ShieldCheck, Megaphone, PenLine, History, UserX, ScanLine, FileText } from 'lucide-react';
import Layout from '../../components/Layout';
import { EnTetePage, Carte, Badge, BandeauInfo, EtatVide, Squelette, Pagination } from '../../design/composants';
import { NavTemps, useDroitsTemps, AccesTemps, lireRh, messageErreur, dateHeure, aujourdhui, ajouterJoursIso, moisLong } from './rhCommun';

const ICONES = { APPELS_NOMINAUX: Megaphone, SAISIES_RH: PenLine, CORRECTIONS_APRES_CLOTURE: History, SANS_SECOND_REGARD: UserX, SCANS_REFUSES: ScanLine, CONSULTATIONS_CERTIFICATS: FileText };
const SENS = { ARRIVEE: 'Arrivée', DEPART: 'Départ', DEBUT_PAUSE: 'Début de pause', FIN_PAUSE: 'Fin de pause' };
const ACTIONS = {
  CONGE_SAISI_RH: 'Congé saisi par la RH', PERIODE_ROUVERTE: 'Période rouverte', POINTAGE_REFUSE: 'Scan refusé', BORNE_PASSAGE_REFUSE: 'Passage refusé à la borne',
  AUTORISATION_VALIDEE: 'Autorisation validée', AUTORISATION_REFUSEE: 'Autorisation refusée', CONSULTATION_FICHIER: 'Certificat consulté',
  DEMANDE_TEMPS_ACCEPTEE: 'Demande du temps acceptée', DEMANDE_TEMPS_REFUSEE: 'Demande du temps refusée', ANOMALIE_QUALIFIEE: 'Anomalie qualifiée',
  CONGE_VALIDE_N1: 'Congé accepté (étape 1)', CONGE_VALIDE_FINAL: 'Congé approuvé', CONGE_REFUSE: 'Congé refusé',
};
const STATUTS_REG = { A_VALIDER: 'À valider', VALIDEE: 'Validée', REFUSEE: 'Refusée' };

/** Détail lisible d'une ligne, selon la section. */
function detailDe(code, l) {
  if (code === 'APPELS_NOMINAUX') return `${SENS[l.sens] || l.sens} à ${dateHeure(l.instant)}${l.site_nom ? ` · ${l.site_nom}` : ''}`;
  if (code === 'SAISIES_RH') return l.nature === 'PASSAGE' ? `${SENS[l.sens] || l.sens} saisi pour ${dateHeure(l.instant)} · motif : ${l.motif || 'non renseigné'}` : `${ACTIONS[l.action] || l.action}${l.motif ? ` · ${l.motif}` : ''}`;
  if (code === 'CORRECTIONS_APRES_CLOTURE') return l.nature === 'REGULARISATION'
    ? `Journée du ${l.date_origine} (${moisLong(l.mois_origine)}) régularisée sur ${moisLong(l.mois_imputation)} · ${STATUTS_REG[l.statut] || l.statut}${l.valideur_nom ? ` par ${l.valideur_nom}` : ''}`
    : `Période rouverte · motif : ${l.motif || 'non renseigné'}`;
  if (code === 'SANS_SECOND_REGARD') return l.nature === 'CONGE' ? `Congé du ${l.date_debut} au ${l.date_fin} accepté sans N+1 habilité` : `${ACTIONS[l.action] || l.action} · ${l.motif || ''}`;
  if (code === 'SCANS_REFUSES') return `${ACTIONS[l.action] || l.action} · raison : ${l.motif || 'non précisée'}`;
  if (code === 'CONSULTATIONS_CERTIFICATS') return 'Certificat médical ouvert';
  return l.motif || '';
}

function Section({ s }) {
  const [page, setPage] = useState(1);
  const taille = 10;
  const Icone = ICONES[s.code] || ShieldCheck;
  const pages = Math.max(1, Math.ceil(s.lignes.length / taille));
  return (
    <Carte titre={s.libelle} Icone={Icone} action={<Badge ton={s.lignes.length ? 'alerte' : 'succes'}>{s.nombre}</Badge>}>
      <p className="rh-form-aide" style={{ margin: '0 0 8px' }}>{s.aide}</p>
      {!s.lignes.length ? <p className="rh-form-aide" style={{ margin: 0 }}>Aucun acte sur la période.</p> : (
        <>
          <div className="rh-conteneur-tableau">
            <table className="rh-tableau">
              <thead><tr><th>Quand</th><th>Salarié</th><th>Auteur</th><th>Détail</th></tr></thead>
              <tbody>
                {s.lignes.slice((page - 1) * taille, page * taille).map((l) => (
                  <tr key={`${l.nature || ''}${l.id}`}>
                    <td style={{ whiteSpace: 'nowrap' }}>{dateHeure(l.quand)}</td>
                    <td>{l.employe_nom || 'Non renseigné'}{l.matricule ? <span className="rh-cellule-secondaire" style={{ display: 'block' }}>{l.matricule}</span> : null}</td>
                    <td>{l.auteur_nom || 'Non renseigné'}</td>
                    <td>{detailDe(s.code, l)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pagination page={page} pages={pages} total={s.lignes.length} taille={taille} onPage={(p) => setPage(Math.min(Math.max(1, p), pages))} />
        </>
      )}
    </Carte>
  );
}

export default function RhRevuePage() {
  const droits = useDroitsTemps();
  const [params, setParams] = useSearchParams();
  const au = params.get('au') || aujourdhui();
  const du = params.get('du') || ajouterJoursIso(au, -6);
  const [donnees, setDonnees] = useState(null);
  const [erreur, setErreur] = useState('');
  const maj = (k, v) => { const p = new URLSearchParams(params); p.set(k, v); setParams(p, { replace: true }); };
  const charger = useCallback(async () => {
    setErreur(''); setDonnees(null);
    try { setDonnees(await lireRh('/revue-actes-sensibles', { du, au })); } catch (e) { setErreur(messageErreur(e, 'Chargement impossible.')); }
  }, [du, au]);
  useEffect(() => { if (droits?.rh?.menu?.revue) charger(); }, [droits, charger]);
  const total = donnees ? donnees.sections.reduce((a, s) => a + s.lignes.length, 0) : 0;

  return (
    <Layout>
      <div className="rh-page">
        <EnTetePage fil={[{ libelle: 'Temps', vers: '/temps' }, { libelle: 'Revue des actes sensibles' }]} titre="Revue des actes sensibles"
          sousTitre="À revoir chaque semaine : les actes faits sans le regard habituel d'une seconde personne, et les accès aux données sensibles." />
        <NavTemps actif="revue" />
        <AccesTemps droits={droits} rubrique="revue">
          <div className="rh-outils">
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13 }}>Du
              <input type="date" className="rh-champ-saisie" value={du} max={au} onChange={(e) => e.target.value && maj('du', e.target.value)} /></label>
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13 }}>au
              <input type="date" className="rh-champ-saisie" value={au} min={du} onChange={(e) => e.target.value && maj('au', e.target.value)} /></label>
          </div>
          {erreur && <BandeauInfo ton="danger">{erreur}</BandeauInfo>}
          {!donnees && !erreur && <Squelette largeur="60%" />}
          {donnees && total === 0 && <EtatVide Icone={ShieldCheck} titre="Aucun acte sensible sur la période" />}
          {donnees && total > 0 && donnees.sections.map((s) => <Section key={s.code} s={s} />)}
        </AccesTemps>
      </div>
    </Layout>
  );
}
